import * as React from 'react';
import * as _ from 'lodash';
import { IntermixModal } from '../layout/IntermixModal';
import { useBus } from 'ts-bus/react';
import {
  guidGenerator,
  getFromLS,
  saveToLS,
  downloadJSONFile,
  transformToDashboardConfig,
} from '../../utils';
import {
  CardMetadata,
  GridCardLayouts,
  WidgetProps,
  DashboardCard,
  DashboardLayouts,
} from '../../types';
import { useWindowDimensions } from '../hooks/useDimensions';
import {
  registerWidgetEvent,
  publishWidgetEvent,
  updateWidgetEvent,
} from './IntermixEvents';
import { RecoilRoot, useRecoilState } from 'recoil';
import {
  widgetState,
  editModeState,
  cardState,
  layoutState,
  selectedCardState,
  namespaceState,
  currentCardOrderIdState,
} from './DashboardState';

export interface DashboardGridContainerProps {
  breakpoints?: any;
  registeredWidgets?: any[];
  preconfiguredDashboard?: DashboardLayouts;
  processDashboardConfig: (data: DashboardLayouts) => any;
  title?: string;
  ns?: string;
}

const DashboardGrid = React.lazy(() =>
  import('./DashboardGrid').then(module => ({ default: module.DashboardGrid }))
);

const WidgetForm = React.lazy(() =>
  import('./WidgetForm').then(module => ({ default: module.WidgetForm }))
);

export const DashboardGridContainerBase: React.FC<DashboardGridContainerProps> = ({
  breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  registeredWidgets,
  preconfiguredDashboard = undefined,
  title,
  processDashboardConfig,
  ns = 'default',
}) => {
  const [initialized, setInitialized] = React.useState(false);
  const { width } = useWindowDimensions();
  const [namespace, setNamespace] = useRecoilState(namespaceState);
  const [, setLayouts] = useRecoilState(layoutState);
  const [orderId, setOrderId] = useRecoilState(currentCardOrderIdState);
  const [widgets, setWidgets] = useRecoilState(widgetState);
  const [cards, setCards] = useRecoilState(cardState);
  const [selectedCard, setSelectedCard] = useRecoilState(selectedCardState);
  const [editMode, setEditMode] = useRecoilState(editModeState);
  const bus = useBus();
  let registerWidgetUnsubscribe: (() => void) | null = null;
  let updatedWidgetUnsubscribe: (() => void) | null = null;

  const registerWidget = (widget: WidgetProps) => {
    setWidgets(items => {
      if (items.findIndex((item: any) => item.id === widget.id) === -1) {
        return [...items, widget];
      } else return items;
    });
  };

  const updateWidget = (widget: any) => {
    setWidgets(items =>
      items.map((item: any) => {
        if (item.id === widget.id) {
          return { ...item, ...widget };
        }
        return item;
      })
    );
  };

  React.useEffect(() => {
    setNamespace(ns);
    setCards([]);
  }, [ns]);

  React.useEffect(() => {
    if (initialized) return;
    if (widgets.length > 0 && !initialized && preconfiguredDashboard) {
      setNamespace(preconfiguredDashboard.name);
      const originalLayouts =
        getFromLS(preconfiguredDashboard.name, 'intermix-grid', 'layouts') ||
        {};
      const savedCards = getFromLS(namespace, 'intermix-dash', 'cards') || [];
      setLayouts(originalLayouts);

      const { cards, layouts } = transformToDashboardConfig(
        preconfiguredDashboard,
        originalLayouts,
        savedCards
      );
      console.log(layouts);
      addLayoutsForCards(layouts);
      createCardsForWidgets(cards);
      setInitialized(true);
    }
  }, [widgets, initialized, preconfiguredDashboard]);

  React.useEffect(() => {
    if (registeredWidgets) {
      registeredWidgets.map(widget => registerWidget(widget));
    }
  }, [registeredWidgets]);

  React.useEffect(() => {
    if (bus) {
      registerWidgetUnsubscribe = bus.subscribe(
        registerWidgetEvent,
        (event: any) => {
          registerWidget(event.payload);
        }
      );
      updatedWidgetUnsubscribe = bus.subscribe(
        updateWidgetEvent,
        (event: any) => {
          updateWidget(event.payload);
        }
      );
      bus.publish(publishWidgetEvent({}));
    }

    return () => {
      if (registerWidgetUnsubscribe) {
        registerWidgetUnsubscribe();
      }

      if (updatedWidgetUnsubscribe) {
        updatedWidgetUnsubscribe();
      }
    };
  }, [bus]);

  const renderCards = () => {
    const savedCards = getFromLS(namespace, 'intermix-dash', 'cards') || [];

    const newCards: any[] = [];
    savedCards.map((card: any, i: any) => {
      const newCard = createWidgetInitial(card, i);
      if (typeof newCard !== 'undefined') {
        newCards.push(newCard);
      }
    });

    setCards(_.orderBy(newCards, ['orderId'], ['asc']));
    setOrderId(newCards.length);
  };

  React.useEffect(() => {
    //   console.log(widgets);
    renderCards();
  }, [widgets]);

  const generateDashboardConfig = (download = false) => {
    const savedLayouts = getFromLS(namespace, 'intermix-grid', 'layouts') || {};
    const savedCards = getFromLS(namespace, 'intermix-dash', 'cards') || [];
    let dashCardlayouts: DashboardCard[];
    dashCardlayouts = savedCards.map((card: any) => {
      const metadata = { ...card };

      let layout = {};
      Object.entries(savedLayouts).map(data => {
        const key = data[0];
        const value = data[1] as any[];
        const cardId: string = card.cardId;
        const cardLayout = value.find(item => item.cardId === cardId);
        if (cardLayout) {
          const finalCardLayout = {
            w: cardLayout.w,
            h: cardLayout.h,
            x: cardLayout.x,
            y: cardLayout.y,
            static: cardLayout.static ? cardLayout.static : false,
            isDraggable: cardLayout.static ? false : true,
            isResizable: cardLayout.static ? false : true,
            cardId: cardLayout.cardId,
          };
          layout = {
            ...layout,
            [key]: {
              ...finalCardLayout,
            },
          };
        }
      });
      return {
        card: {
          metadata,
          layout,
        },
      };
    });

    let dashLayout: DashboardLayouts;
    dashLayout = {
      name: namespace,
      config: dashCardlayouts,
    };

    if (download) {
      downloadJSONFile(dashLayout, 'mydash');
    } else {
      if (typeof processDashboardConfig === 'function') {
        processDashboardConfig(dashLayout);
      }
    }
  };

  const getCurrentBreakpoint = (): string => {
    let breakpoint = 'xxs';
    if (width >= breakpoints['lg']) {
      breakpoint = 'lg';
    } else if (width >= breakpoints['md']) {
      breakpoint = 'md';
    } else if (width >= breakpoints['sm']) {
      breakpoint = 'sm';
    } else if (width >= breakpoints['xs']) {
      breakpoint = 'xs';
    }
    return breakpoint;
  };

  const createWidgetInitial = (card: any, orderId: any) => {
    const findWidget = widgets.find(widget => widget.id === card.widgetId);
    if (typeof findWidget === 'undefined') {
      return;
    }
    const newCard = Object.assign({}, findWidget);
    if (typeof newCard.static === 'undefined') {
      newCard.static = false;
    }
    const newCardId =
      typeof card.cardId !== 'undefined' ? card.cardId : guidGenerator();
    return {
      ...newCard,
      ...card,
      widgetId: findWidget.id,
      cardId: newCardId,
      orderId,
    };
  };

  /**
   * Add Layouts to the Dashboard
   * @param layouts : GridCardLayouts
   */
  const addLayoutsForCards = (layouts: GridCardLayouts) => {
    console.log(layouts, namespace);
    saveToLS(namespace, 'intermix-grid', 'layouts', layouts);
    setLayouts(layouts);
  };

  /**
   * Create Cards to the Dashboard
   * @param cards : CardMetaData[]
   */
  const createCardsForWidgets = (cards: CardMetadata[]) => {
    if (!cards) return;
    const cardList: any[] = cards.map(card => createNewCardFromWidgetId(card));
    saveToLS(
      namespace,
      'intermix-dash',
      'cards',
      _.orderBy(
        cardList.map(item => {
          return {
            widgetId: item.widgetId,
            orderId: item.orderId,
            cardId: item.cardId,
            data: item.data,
            options: item.options,
            name: item.name,
          };
        }),
        ['orderId'],
        ['asc']
      )
    );
    setCards(_.orderBy(cardList, ['orderId'], ['asc']));
  };

  const createNewCardFromWidgetId = (card: CardMetadata) => {
    const findWidget = widgets.find(widget => widget.id === card.widgetId);
    const newCard = Object.assign({}, findWidget);
    if (typeof newCard === 'undefined') return;
    return {
      ...newCard,
      ...card,
    };
  };

  const createCardFromWidgetId = (
    widgetId: any,
    modifiedWidget = null as any,
    addNewCard = true
  ) => {
    const newCard = widgets.find(widget => widget.id === widgetId);
    const newCardId = guidGenerator();
    if (typeof newCard === 'undefined') return;
    if (!addNewCard) {
      //  console.log(selectedCard.cardId, cards);
      const selectedCardInfo: any = cards.find(
        (card: any) => card.cardId === selectedCard.cardId
      );
      const removeOldCard = cards.filter(
        (card: any) => card.cardId !== selectedCard.cardId
      );
      const newCardList = [
        ...removeOldCard,
        {
          ...selectedCardInfo,
          widgetId,
          ...newCard,
          ...modifiedWidget,
        },
      ];
      saveToLS(
        namespace,
        'intermix-dash',
        'cards',
        _.orderBy(
          newCardList.map(item => {
            return {
              widgetId: item.widgetId,
              orderId: item.orderId,
              cardId: item.cardId,
              data: item.data,
              options: item.options,
              name: item.name,
            };
          }),
          ['orderId'],
          ['asc']
        )
      );
      setCards(_.orderBy(newCardList, ['orderId'], ['asc']));
    } else {
      const newCardList = [
        ...cards,
        {
          widgetId,
          orderId: orderId,
          cardId: newCardId,
          ...newCard,
          ...modifiedWidget,
        },
      ];
      saveToLS(
        namespace,
        'intermix-dash',
        'cards',
        _.orderBy(
          newCardList.map(item => {
            return {
              widgetId: item.widgetId,
              orderId: item.orderId,
              cardId: item.cardId,
              data: item.data,
              options: item.options,
              name: item.name,
            };
          })
        )
      );
      setCards(_.orderBy(newCardList, ['orderId'], ['asc']));
    }
  };

  return (
    <div className="id-flex id-flex-grow">
      <React.Suspense fallback="Loading...">
        <DashboardGrid
          title={title}
          onRemoveItem={i => {
            //    console.log(i, cards);
            const newCards = cards.filter(item => item.cardId !== i);
            saveToLS(
              namespace,
              'intermix-dash',
              'cards',
              newCards.map(item => {
                return {
                  widgetId: item.widgetId,
                  orderId: item.orderId,
                  cardId: item.cardId,
                  data: item.data,
                  options: item.options,
                  name: item.name,
                };
              })
            );
            setCards(_.orderBy(newCards, ['orderId'], ['asc']));
          }}
          onConfigItem={i => {
            setSelectedCard(i);
            setEditMode(true);
          }}
          onAddItem={() => {
            setSelectedCard(undefined);
            setOrderId(orderId + 1);
            setEditMode(true);
          }}
          defaultBreakpoint={getCurrentBreakpoint()}
          onDashboardConfig={generateDashboardConfig}
          onSaveConfig={generateDashboardConfig}
        />
      </React.Suspense>
      {editMode && (
        <React.Suspense fallback="Loading...">
          <IntermixModal onCloseHandler={() => setEditMode(false)}>
            <WidgetForm
              onCancel={() => setEditMode(false)}
              onSubmit={data => {
                if (typeof data.modifiedWidget !== 'undefined') {
                  updateWidget(data.modifiedWidget);
                }
                createCardFromWidgetId(
                  data.id,
                  data.modifiedWidget,
                  selectedCard === undefined
                );
                setEditMode(false);
                setSelectedCard(undefined);
              }}
            />
          </IntermixModal>
        </React.Suspense>
      )}
    </div>
  );
};

export const DashboardGridContainer: React.FC<any> = ({ ...props }) => {
  return (
    <RecoilRoot>
      <DashboardGridContainerBase {...props} />
    </RecoilRoot>
  );
};
